import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, useIntl } from '@umijs/max';
import { DownOutlined } from "@ant-design/icons";
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  SelectLang,
} from '@/components';
import { currentUser as queryCurrentUser } from '@/services/user/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { Divider, Image } from 'antd';
import { Company, CurrentTimezone } from './components/RightContent';
import NoAccessible from './pages/403';

const isDev =
  process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * 用于设置全局返回状态，可以在全局使用
 * const { initialState, loading, error, refresh, setInitialState } = useModel("@@initialState");
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  region?: Record<string, string>;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![
      loginPath,
      "/user/password-result",
      "/user/reset-password",
      "/user/update-password",
      "/user/forgot-password",
    ].includes(location.pathname)
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const intl = useIntl();
  return {
    title: false,
    logo: () => <Image src="/assets/menu-icon.webp" preview={false} />,
    //-- 自定义 logo 和 title
    headerTitleRender: (logo, title) => {
      return (
        <>
          {logo}
          <CurrentTimezone />
        </>
      );
    },
    //-- 顶部右侧 语言切换
    actionsRender: () => [
      <Company key="Company">
        {initialState?.currentUser?.partner?.legal_name ||
          initialState?.currentUser?.partner?.name}
        {!initialState?.currentUser?.partner?.contract_id && (
          <span style={{ color: "red" }}>
            &nbsp;({intl.formatMessage({ id: "global.contract_not_bound" })})
          </span>
        )}
      </Company>,
      //-- 分割线
      <Divider key="divider" type="vertical" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      gap: 0,
      src: <AvatarName />,
      title: <DownOutlined style={{ fontSize: 10 }} />,
      render: (_, avatarChildren) => {
        return (
          <>
            <AvatarDropdown menu={true}>{avatarChildren}</AvatarDropdown>
          </>
        );
      },
    },
    //-- 配置水印，会在页面显示水印
    waterMarkProps: {
      content: undefined,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    //-- 左侧底部链接
    links: isDev
      ? [
          // <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          //   <LinkOutlined />
          //   <span>OpenAPI 文档</span>
          // </Link>,
        ]
      : [],
    //-- 内容区样式
    contentStyle: {
      padding: 16,
    },
    //-- 自定义menu 顶部渲染Render
    menuHeaderRender: undefined,
    // 自定义 403 页面
    unAccessible: <NoAccessible />,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  //-- 使用绝对路径会导致 proxy 配置无效
  baseURL: '/',
  ...errorConfig,
};
